import React, { useState, useRef } from "react";
import { CloudUpload, Download, FileText, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ReactComponent as Eye } from "../../../../../icons/View_eye.svg";
import { ReactComponent as Delete } from "../../../../../icons/View_dlt.svg";
import ModHeader from "../../Mod/ModTable/ModHeader";
import { useSelector } from "react-redux";
import {
  fetchgetTemplateIPP,
  fetchgetTemplateISGS,
  fetchgetTemplateSGU,
  ModBulkUploadFile,
} from "../../../../../Rest_api/DemoRestApi";
import Tooltip from "../../../../../components/tooltip";
import { BASE_URL } from "../../../../../configs";
import { useMeritEngineNavigation } from "../../useMeritEngineNavigation";
import MainHeader from "../../DateSession";
import { ErrorPopup } from "../../../../../components/ErrorHandling";
import { getAccessToken } from "../../../../Protected";

// Helper function to format the date as DD-MMM-YYYY
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(date);
  return formattedDate.replace(/ /g, "-");
};

type UploadedFile = {
  type: string;
  date: string;
  file_url: string;
  file: File;
  id: number;
};

const FileRegexPatterns: { [key: string]: RegExp } = {
  ISGS: /^ISGS\.xlsx$/,
  SGU: /^SGU\.xlsx$/i,
  IPP: /^IPP\.xlsx$/,
  Vc: /^VC_all_generators\.xlsx$/,
};

const RegexToFileTypeMap: { [key: string]: string } = {
  ISGS: "ISGS",
  SGU: "SGU",
  IPP: "IPP",
  Vc: "VC for Generators (Optional)",
};

type FileRowProps = {
  fileName: string;
  uploadedFile: File | null;
  fileError: string | null;
  onFileChange: (fileName: string, file: File | null) => void;
  onFileError: (fileName: string, error: string | null) => void;
};

const FileRow: React.FC<FileRowProps> = ({
  fileName,
  uploadedFile,
  fileError,
  onFileChange,
  onFileError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const preSelectedDate = useSelector(
    (state: any) => state.modDemoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (
        fileExtension !== "xlsx" &&
        fileExtension !== "csv" &&
        fileExtension !== "pdf"
      ) {
        const errorMsg = "Invalid file type. Only .xlsx and .csv files are allowed.";
        setError(`Error uploading "${fileName}":\n${errorMsg}`);
        onFileError(fileName, errorMsg);
        onFileChange(fileName, null);
        return;
      }

      // Validate file name pattern
      const regexKey = Object.keys(RegexToFileTypeMap).find(
        (key) => RegexToFileTypeMap[key] === fileName
      );
      if (regexKey && FileRegexPatterns[regexKey]) {
        const pattern = FileRegexPatterns[regexKey];
        if (!pattern.test(file.name)) {
          const errorMsg = `File name does not match required pattern for ${fileName}.`;
          setError(`Error uploading "${fileName}":\n${errorMsg}`);
          onFileError(fileName, errorMsg);
          onFileChange(fileName, null);
          return;
        }
      }

      onFileError(fileName, null);
      onFileChange(fileName, file);
    } else {
      onFileChange(fileName, null);
      onFileError(fileName, null);
    }
  };

  const handleViewFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, "_blank");
    }
  };

  const handleDeleteFile = () => {
    onFileChange(fileName, null);
    onFileError(fileName, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isReady = uploadedFile && !fileError;
  const isError = !uploadedFile && fileError;

  const downloadTemplateIPP = async () => {
    if (fileName === "VC for Generators (Optional)") {
      try {
        setError(null);

        const url = `${BASE_URL}/mod_demo/getGeneratorVC?date=${preSelectedDate}&session=${modSelectedSession}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `${getAccessToken() || ""}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to fetch VC data: ${response.status} - ${errorText}`
          );
        }

        const vcData = await response.json();
        console.log("✅ VC data received:", vcData);

        if (!Array.isArray(vcData) || vcData.length === 0) {
          throw new Error("No VC data available");
        }

        const headerRow = ["Generator Name", "VC (P/U)"];
        const dataRows = vcData.map((item) => [
          item.generator || "",
          item.vc || "",
        ]);

        const worksheetData = [headerRow, ...dataRows];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        const columnWidths = [
          { wch: 30 },
          { wch: 15 },
        ];
        worksheet["!cols"] = columnWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VC Data");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });

        saveAs(blob, "VC_all_generators.xlsx");

        console.log("✅ VC Generator Excel created successfully");
      } catch (error) {
        console.error("❌ Error creating VC Generator Excel:", error);
        setError(
          error instanceof Error
            ? `Failed to create VC Excel: ${error.message}`
            : "Failed to create VC Generator Excel. Please try again later."
        );
      }
      return;
    }

    try {
      let triggerApi;
      let endpoint;
      let fileLabel;

      switch (fileName) {
        case "ISGS":
          triggerApi = fetchgetTemplateISGS;
          endpoint = "downloadISGS";
          fileLabel = "ISGS";
          break;

        case "SGU":
          triggerApi = fetchgetTemplateSGU;
          endpoint = "downloadSGU";
          fileLabel = "SGU";
          break;

        case "IPP":
          triggerApi = fetchgetTemplateIPP;
          endpoint = "downloadIPP";
          fileLabel = "IPP";
          break;

        default:
          console.error("❌ Unknown fileName:", fileName);
          return;
      }

      const res = await triggerApi(preSelectedDate, modSelectedSession);
      if (!res) {
        throw new Error(`Failed to trigger Excel API for ${fileLabel}`);
      }

      const fileResponse = await fetch(
        `${BASE_URL}/mod_demo/${endpoint}?date=${preSelectedDate}&session=${modSelectedSession}`,
        { method: "GET", cache: "no-store" }
      );

      if (!fileResponse.ok) {
        throw new Error(`Failed to download ${fileLabel} Excel file`);
      }

      const blob = await fileResponse.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileLabel}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Error downloading Excel file:", err);
    }
  };

  return (
    <div className="flex items-center justify-between py-2 px-1 border mb-3 border-[#F3F5F8] last:border-b-0">
      <div className="flex items-center gap-3">
        <FileText size={20} style={{ color: "#8C8A96" }} />
        <span className={`text-sm font-medium ${fileName === "VC for Generators (Optional)" ? "text-[#F1935C]" : "text-[#525062]"}`}>
          {fileName}
        </span>
      </div>

      {isReady ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleViewFile}
              className="text-[#F1935C] hover:opacity-80 transition-opacity p-1 rounded"
            >
              <Eye />
            </button>
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#00BF6F", color: "#fff" }}
            >
              <CheckCircle size={14} /> <span>Ready</span>
            </button>

            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#FDE6E8", color: "#B32130" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x-circle"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m15 9-6 6" />
                <path d="m9 9 6 6" />
              </svg>{" "}
              <span>Error</span>
            </button>
            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={downloadTemplateIPP}
            className="flex items-center justify-center gap-2 w-[110px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-colors  bg-[#FFFAF6] text-[#F1935C]"
          >
            <Download className="w-4 h-4" />
            <span>Template</span>
          </button>
          <button
            onClick={handleUploadClick}
            className="flex items-center justify-center gap-2 w-[90px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
            style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
          >
            <CloudUpload size={14} /> Upload
          </button>
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".xlsx,.csv,.pdf"
      />
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default function FileUploadTabComponent() {
  const [error, setError] = useState<string | null>(null);
  const preSelectedDate = useSelector(
    (state: any) => state.modDemoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession
  );
  const [loading, setLoading] = useState(false);
  const { navigateToPage } = useMeritEngineNavigation();

  const fileTypes = [
    // "VC for Generators (Optional)",
    "ISGS",
    "SGU",
    "IPP",
  ];

  const FileTypeBackendAlias: { [key: string]: string } = {
    ISGS: "isgs",
    SGU: "sgu",
    IPP: "ipp",
    "VC for Generators (Optional)": "vc-generator",
  };

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [fileErrors, setFileErrors] = useState<Record<string, string | null>>(
    {}
  );

  const handleFileChange = (fileName: string, file: File | null) => {
    const regexKey = Object.keys(RegexToFileTypeMap).find(
      (key) => RegexToFileTypeMap[key] === fileName
    );
    if (file && regexKey && FileRegexPatterns[regexKey]) {
      const pattern = FileRegexPatterns[regexKey];
      if (!pattern.test(file.name)) {
        const errorMsg = `File name does not match required pattern for ${fileName}.`;
        setError(errorMsg);
        setFileErrors((prevErrors) => ({
          ...prevErrors,
          [fileName]: errorMsg,
        }));
        return;
      }
    }
    setUploadedFiles((prevFiles) => {
      const filtered = prevFiles.filter((f) => f.type !== fileName);
      if (file) {
        return [
          ...filtered,
          {
            type: fileName,
            date: preSelectedDate,
            file_url: URL.createObjectURL(file),
            file,
            id: Date.now(),
          },
        ];
      }
      return filtered;
    });
    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: null,
    }));
  };

  const handleFileError = (fileName: string, error: string | null) => {
    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: error,
    }));
  };

  function detectFileType(fileName: string): string {
    for (const [regexKey, pattern] of Object.entries(FileRegexPatterns)) {
      if (pattern.test(fileName)) {
        return RegexToFileTypeMap[regexKey] || regexKey;
      }
    }
    return "";
  }

  const [selectedFileType, setSelectedFileType] = useState<string>("All Files");

  const filesToDisplay =
    selectedFileType === "All Files"
      ? fileTypes
      : fileTypes.filter((fileType) => fileType === selectedFileType);

  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  const handleBulkUploadClick = () => {
    bulkFileInputRef.current?.click();
  };

  function handleBulkUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;

    if (!preSelectedDate) {
      setError("Please select a date");
      event.target.value = "";
      return;
    }

    if (!files || files.length === 0) {
      setError("Please select files");
      return;
    }

    const validExtensions = [".xlsx", ".xls", ".pdf"];
    const newFiles: UploadedFile[] = [];
    const undetectedFiles: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError(
          `Invalid file format for ${file.name}. Please select only XLSX, XLS or PDF files.`
        );
        event.target.value = "";
        return;
      }

      const detectedType = detectFileType(file.name);

      if (!detectedType) {
        undetectedFiles.push(file.name);
        continue;
      }

      newFiles.push({
        type: detectedType,
        date: preSelectedDate,
        file_url: URL.createObjectURL(file),
        file,
        id: Date.now() + i,
      });
    }

    if (undetectedFiles.length > 0) {
      setError(
        `Could not auto-detect file type for:\n${undetectedFiles.join(
          "\n"
        )}\n\nPlease upload these files individually.`
      );
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => {
        const newTypes = new Set(newFiles.map((f) => f.type));
        prev.forEach((f) => {
          if (newTypes.has(f.type)) URL.revokeObjectURL(f.file_url);
        });
        const kept = prev.filter((f) => !newTypes.has(f.type));
        return [...kept, ...newFiles];
      });

      setFileErrors((prev) => {
        const updated = { ...prev };
        newFiles.forEach((f) => {
          updated[f.type] = null;
        });
        return updated;
      });
    }

    event.target.value = "";
  }

  const GotoModTable = async () => {
    const requiredFileTypes = fileTypes.filter(
      (type) => type !== "VC for Generators (Optional)"
    );

    const vcFile = uploadedFiles.find(
      (f) => f.type === "VC for Generators (Optional)"
    );
    const isVcUploaded = vcFile !== undefined;

    const uploadedRequiredTypes = uploadedFiles.filter((f) =>
      requiredFileTypes.includes(f.type)
    );

    if (isVcUploaded) {
      const totalUploaded = uploadedRequiredTypes.length + 1;
      if (totalUploaded !== 4) {
        setError(
          `VC file is uploaded. Please upload all remaining files. Currently uploaded: ${totalUploaded}/4`
        );
        return;
      }
    } else {
      if (uploadedRequiredTypes.length !== 3) {
        setError(
          `Please upload all required files. Currently uploaded: ${uploadedRequiredTypes.length}/3 (VC file is optional)`
        );
        return;
      }
    }

    try {
      setLoading(true);
      const formData = new FormData();

      const metaData = uploadedFiles.map((file, idx) => {
        const field = `file_${idx}`;
        formData.append(field, file.file);
        return {
          field,
          date: file.date,
          type: FileTypeBackendAlias[file.type] || file.type,
        };
      });

      formData.append("metaData", JSON.stringify(metaData));
      formData.append("date", preSelectedDate);
      formData.append("session", modSelectedSession);

      const res = await ModBulkUploadFile(formData);
      setUploadedFiles([]);
      setSelectedFileType("All Files");

      if (res) {
        navigateToPage("merit-order-dispatch");
        (window as any).resetNextTabs?.("GENERATION");
      }
    } catch (error) {
      console.error("Error generating file:", error);
      setError("Error generating file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const uploadedRequiredCount = uploadedFiles.filter((f) =>
    ["ISGS", "SGU", "IPP"].includes(f.type)
  ).length;

  const vcFile = uploadedFiles.find(
    (f) => f.type === "VC for Generators (Optional)"
  );
  const isVcUploaded = vcFile !== undefined;

  const isButtonEnabled = isVcUploaded
    ? uploadedRequiredCount === 3 && isVcUploaded
    : uploadedRequiredCount === 3;

  return (
    <div className="flex flex-col w-full">
      <MainHeader />

      <div className="h-[calc(-73px+100vh)] p-4">
        <div className="w-full h-full flex flex-col gap-4">
          <ModHeader title="Available Generation Capacity" />
          <div className="gas-chart-container ">
            <div className="gas-heading gap-x-10">
              <div className="flex gap-x-6 w-full justify-between">
                <div className="flex gap-x-4">
                  <select
                    className="border border-[#C5C5CBD9] rounded-lg p-2 max-w-[200px] text-[#8C8A96]"
                    value={selectedFileType}
                    onChange={(e) => setSelectedFileType(e.target.value)}
                  >
                    <option value="All Files">All Files</option>
                    {fileTypes.map((fileType, index) => (
                      <option key={index} value={fileType}>
                        {fileType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-x-4">
                  <button
                    type="submit"
                    disabled={!isButtonEnabled || loading}
                    onClick={GotoModTable}
                    className="flex items-center gap-2 px-3 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
                  >
                    {loading ? "Processing..." : "Verify & Generate MOD"}
                  </button>
                </div>
              </div>
            </div>

            <div
              className="p-4 mt-3"
              style={{
                backgroundColor: "#F1F5F980",
                border: "1px solid #CAD5E2",
                borderRadius: "15px",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3
                    className="text-xl font-medium"
                    style={{ color: "#6F6D7C" }}
                  >
                    Add Files
                  </h3>
                  <p className="text-large" style={{ color: "#8C8A96" }}>
                    Attach all the mentioned files below, either individual
                    files or all the files together, to generate the MOD Table.
                    VC for Generators is optional.
                  </p>
                </div>
                <button
                  onClick={handleBulkUploadClick}
                  className="flex items-center justify-center gap-2 w-[180px] h-[37px] text-base rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
                  style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                >
                  <CloudUpload size={20} /> Bulk Upload
                </button>
              </div>
              <input
                type="file"
                ref={bulkFileInputRef}
                onChange={handleBulkUpload}
                className="hidden"
                multiple
                accept=".xlsx, .xls, .pdf"
              />
            </div>
            <h2
              className="mx-4 py-2 chartHeading border-b !text-lg !font-medium"
              style={{ color: "#6F6D7C" }}
            >
              File Types
            </h2>

            <div className="px-4 h-100 overflow-y-auto">
              {filesToDisplay.map((fileName) => {
                const uploadedFileObj =
                  uploadedFiles.find((f) => f.type === fileName) || null;
                return (
                  <FileRow
                    key={fileName}
                    fileName={fileName}
                    uploadedFile={uploadedFileObj ? uploadedFileObj.file : null}
                    fileError={fileErrors[fileName] || null}
                    onFileChange={handleFileChange}
                    onFileError={handleFileError}
                  />
                );
              })}
            </div>
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={
                  <p>
                    Default values and variable charges auto-fetched from
                    system.
                  </p>
                }
                text=" "
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </div>
      </div>
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </div>
  );
}